import { Skeleton } from "../../../components/ui/Skeleton";
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@repo/utils';
import { getSession } from '../../../lib/session';
import { sessionQueryKey } from '../../../lib/query-client';
import { ReportCard } from '../components/ReportCard';
import { Button } from '../../../components/ui/Button';
import { DataView } from '../../../components/ui/DataView';
import {
  useReportDefinitions,
} from '../hooks';
import type {
  ReportCategory,
  ReportDefinitionListItem,
} from '../types';
import { PageHeader } from '../../../components/layout/PageHeader';

const CATEGORIES: { key: ReportCategory; label: string }[] = [
  { key: 'OPERATIONAL', label: 'Operational' },
  { key: 'STATUTORY', label: 'Statutory' },
  { key: 'MANAGEMENT', label: 'Management' },
];

const ROLE_RANK: Record<string, number> = {
  group_admin: 100,
  company_admin: 80,
  staff: 50,
  agent: 30,
  customer: 10,
};

function canRunReport(requiredRole: string | null, userType: string): boolean {
  if (!requiredRole) return true;
  return (ROLE_RANK[userType] ?? 0) >= (ROLE_RANK[requiredRole] ?? 0);
}

export function ReportCentrePage() {
  const { data: session } = useQuery({
    queryKey: sessionQueryKey,
    queryFn: getSession,
    staleTime: Infinity,
  });

  const userType = session?.user.type ?? 'staff';

  const [category, setCategory] = useState<ReportCategory>('OPERATIONAL');
  const [defsPage] = useState(1);

  // Fetch definitions for the current category
  const defsQuery = useReportDefinitions({
    page: defsPage,
    limit: 50,
    category,
  });

  const visibleDefinitions = useMemo<ReportDefinitionListItem[]>(() => {
    const defs = defsQuery.data?.data ?? [];
    return defs.filter((d) => d.isActive && canRunReport(null, userType));
  }, [defsQuery.data, userType]);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Report Centre"
        subtitle="Browse and view reports."
      />
      <div className="flex-1 w-full min-h-0">
      <div className="flex flex-col gap-6 h-full overflow-auto">

      {/* Category tabs */}
      <div className="flex gap-1">
        {CATEGORIES.map(({ key, label }) => (
          <Button
            key={key}
            variant="ghost"
            onClick={() => setCategory(key)}
            className={cn(
              'px-3 py-1.5 rounded-[6px] text-sm font-medium transition-colors bg-transparent hover:bg-transparent',
              category === key
                ? 'bg-accent-500 text-white'
                : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary',
            )}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Report cards grid */}
      <DataView
        query={defsQuery}
        loadingSkeleton={
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-36" />
            ))}
          </div>
        }
      >
        {() => {
          const items = visibleDefinitions.filter((d) => d.category === category);
          if (items.length === 0) {
            return (
              <p className="text-sm text-text-muted">
                No reports available in this category.
              </p>
            );
          }
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((def) => (
                <ReportCard
                  key={def.reportCode}
                  definition={def}
                />
              ))}
            </div>
          );
        }}
      </DataView>

      </div>
      </div>
    </div>
  );
}
